import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const DatePickerModal = ({ isOpen, onClose, onApply, customQuickSelections }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [activeSelection, setActiveSelection] = useState('');

  if (!isOpen) return null;
 
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const defaultQuickSelections = [
    { label: "Last 7 days", onClick: () => handleRangeSelect(7) },
    { label: "Last 14 days", onClick: () => handleRangeSelect(14) },
    { label: "Last 30 days", onClick: () => handleRangeSelect(30) },
    { label: "Last 3 months", onClick: () => handleRangeSelect(90) },
    { label: "Last 12 months", onClick: () => handleRangeSelect(365) },
  ];

  const quickSelections = customQuickSelections || defaultQuickSelections;

  const handleRangeSelect = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    setStartDate(start);
    setEndDate(end);
  };

  const handleSelectionClick = (item) => {
    setActiveSelection(item.label);
    if (item.onClick) {
        item.onClick();
    }
  };
  const currentMonth = monthNames[currentDate.getMonth()];
  const currentYear = currentDate.getFullYear();

  const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const handleDayClick = (day) => {
    const selectedDate = new Date(currentYear, currentDate.getMonth(), day);
    
    if (!startDate || (startDate && endDate)) {
      setStartDate(selectedDate);
      setEndDate(null);
    } else {
      if (selectedDate < startDate) {
        setStartDate(selectedDate);
      } else {
        setEndDate(selectedDate);
      }
    }
  };

  const isSelected = (day) => {
    const date = new Date(currentYear, currentDate.getMonth(), day);
    if (!startDate) return false;
    if (endDate) {
      return date >= startDate && date <= endDate;
    }
    return date.getTime() === startDate.getTime();
  };

  const isStartOrEnd = (day) => {
    const date = new Date(currentYear, currentDate.getMonth(), day);
    if (!startDate) return false;
    return date.getTime() === startDate.getTime() || (endDate && date.getTime() === endDate.getTime());
  };

  const renderCalendar = () => {
    const totalDays = daysInMonth(currentDate.getMonth(), currentYear);
    const startingDay = firstDayOfMonth(currentDate.getMonth(), currentYear);
    const days = [];

    const prevMonthDays = daysInMonth(currentDate.getMonth() - 1, currentYear);
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`prev-${i}`} className="text-center py-2 text-gray-400">{prevMonthDays - startingDay + i + 1}</div>);
    }

    for (let day = 1; day <= totalDays; day++) {
      const selected = isSelected(day);
      const startOrEnd = isStartOrEnd(day);
      days.push(
        <div 
          key={day} 
          onClick={() => handleDayClick(day)}
          className={`text-center py-2 rounded-lg cursor-pointer ${
            startOrEnd ? 'bg-blue-600 text-white' : 
            selected ? 'bg-blue-100 text-blue-700' : 
            'hover:bg-gray-100'
          }`}
        >
          {day}
        </div>
      );
    }
    
    // Fill remaining cells
    const remainingCells = 42 - days.length;
    for (let i = 1; i <= remainingCells; i++) {
        days.push(<div key={`next-${i}`} className="text-center py-2 text-gray-400">{i}</div>);
    }

    return days;
  };

  const handleApply = () => {
    if (onApply && startDate) {
      onApply(startDate, endDate || startDate);
    }
    onClose();
  };


  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50" dir="ltr">
      <div className="bg-white rounded-2xl shadow-lg flex" onClick={(e) => e.stopPropagation()}>
        <div className="w-[200px] p-4 border-r border-gray-200">
          <ul className="space-y-2">
            {quickSelections.map((item, index) => (
              <li 
                key={index} 
                className={`p-2 rounded-lg text-sm font-medium cursor-pointer ${activeSelection === item.label ? 'bg-gray-100 text-gray-800' : 'text-gray-700 hover:bg-gray-100'}`}
                onClick={() => handleSelectionClick(item)}
              >
                {item.label}
              </li>
            ))}
          </ul>
        </div>
        <div className="p-6 w-[500px]">
          <div className="flex justify-between items-center mb-4">
            <button onClick={prevMonth} className="p-2 rounded-full hover:bg-gray-100">
              <ChevronLeft size={20} />
            </button>
            <div className="font-semibold text-lg">{currentMonth} {currentYear}</div>
            <button onClick={nextMonth} className="p-2 rounded-full hover:bg-gray-100">
              <ChevronRight size={20} />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-sm">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
              <div key={i} className="text-center font-medium text-gray-500 py-2">{day}</div>
            ))}
            {renderCalendar()}
          </div>
          <div className="flex justify-end gap-4 mt-6" dir="rtl">
            <button onClick={onClose} className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-800 font-semibold hover:bg-gray-100 transition-colors">
              الغاء
            </button>
            <button onClick={handleApply} className="px-6 py-2.5 bg-[var(--Primary-Primary)] text-white rounded-lg font-semibold shadow-sm hover:bg-blue-700 transition-colors">
              تأكيد
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatePickerModal;
